import React, { useCallback, useState } from 'react';

import {
  LoadingAnimation,
  SweetpassLandingPage,
  type SweetpassLandingPageCtaAction,
  useSweetpassLandingPageContent,
} from '@order/components';
import { type DurationUnit, type TierName } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';
import { useTrackEffect } from '@order/Telemetry';

import { SweetpassConfirmationDialog } from '../../components/SweetpassConfirmationDialog';
import { getSweetpassUpgradeContentfulTag } from './utils';

// ────────────────────────────────────────────────────────────────────────────────

export const SweetpassUpgradeScreenContent = (
  props: SweetpassUpgradeScreenContentProps,
) => {
  const {
    campaignId,
    sweetpassNextTierName,
    isNextTierSweetpassPlus,
    isEligibleForFreeTrial,
    hasFreeTrial,
    planPrice,
    trialDuration,
    trialDurationSingular,
    billingFrequencyUnit,
    isConfirmingOptIn,
    onCtaConfirmation,
  } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { t } = useLocalizationContext();

  // ─── State ───────────────────────────────────────────────────────────

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // ─── Callbacks ───────────────────────────────────────────────────────

  const handleDismissModal = useCallback(() => {
    setShowConfirmationModal(false);
  }, []);

  const handleCtaPress = useCallback(
    (action: SweetpassLandingPageCtaAction) => {
      if (action === 'join_sweetpass') {
        setShowConfirmationModal(true);

        return;
      }

      onCtaConfirmation(action);
    },
    [onCtaConfirmation],
  );

  // ─────────────────────────────────────────────────────────────────────

  const headerTitle = isNextTierSweetpassPlus
    ? t('sweetpass-plus')
    : t('sweetpass');

  const tag = getSweetpassUpgradeContentfulTag({
    campaignId,
    sweetpassNextTierName,
    isEligibleForFreeTrial,
    hasFreeTrial,
  });

  const sweetpassLandingPageContent = useSweetpassLandingPageContent({
    tag,
    planPrice,
    trialDuration,
    trialDurationSingular,
    billingFrequencyUnit,
    onCtaPress: handleCtaPress,
  });

  const { content, fetching: isLoadingContent } = sweetpassLandingPageContent;

  // ─────────────────────────────────────────────────────────────────────

  const onSweetpassDialogConfirm = useCallback(() => {
    onCtaConfirmation(content?.cta?.action);
  }, [content?.cta?.action, onCtaConfirmation]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEffect('sweetpass_account.review_modal.viewed');

  // ─────────────────────────────────────────────────────────────────────

  if (isLoadingContent) {
    return <LoadingAnimation />;
  }

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <SweetpassLandingPage
        title={headerTitle}
        headerContent={content.header}
        benefitsContent={content.benefits}
        ctaContent={content.cta}
      />

      <SweetpassConfirmationDialog
        visible={showConfirmationModal}
        isLoading={isConfirmingOptIn}
        onDismiss={handleDismissModal}
        onConfirm={onSweetpassDialogConfirm}
      />
    </>
  );
};

type SweetpassUpgradeScreenContentProps = Readonly<{
  campaignId?: string;
  sweetpassNextTierName?: TierName;
  isEligibleForFreeTrial: boolean;
  hasFreeTrial: boolean;
  isNextTierSweetpassPlus: boolean;
  planPrice: number;
  trialDuration?: string;
  trialDurationSingular?: string;
  billingFrequencyUnit?: DurationUnit;
  isConfirmingOptIn: boolean;
  onCtaConfirmation: (action?: string) => void;
}>;
